import { graphql } from "gatsby"
import { string } from "prop-types"
import React from "react"
import { Link } from "react-scroll"
import styled, { css } from "styled-components"
import Breakpoints from "../../components/shared/breakpoints"
import Chevron from "../../components/shared/svg/chevron"
import { T } from "../styles/typography"
import { normalizeForwardSlashes } from "../util/internal-routes"
import withVariants from "../util/withVariants"
import DefaultLink from "./link"
import { IStyleVariant } from "./variant-container"

const FullWidthStyle = css`
  display: flex;
  width: 100%;
`
const TextLinkStyle = css<{ $primary: string }>`
  && {
    width: max-content;
    padding: 0;
    min-width: 0;
    font-size: 14px;
    background: transparent;
    color: ${(props) => props.$primary};
    border: none;

    svg,
    ${Chevron} {
      width: 10px;
      height: 10px;
      margin: -1px 0 0 12px;
    }
  }
`

const OutlineStyle = css<{ $primary: string }>`
  color: ${(props) => props.$primary};
  background-color: transparent;
`

const DefaultStyle = css<{ $primary: string; $secondary: string }>`
  border: 1px solid ${(props) => props.$primary};
  color: ${(props) => props.$secondary};
  background-color: ${(props) => props.$primary};
  padding: 16px 32px;

  min-width: min(282px, 100%);
  width: max-content;
  max-width: 100%;

  @media (min-width: ${Breakpoints.lg}) {
    min-width: 181px;
  }
`

export const ButtonStyles = {
  DefaultStyle,
  OutlineStyle,
  TextLinkStyle,
  FullWidthStyle,
}

const BaseStyle = css<{
  $fullWidth?: boolean
  $outline?: boolean
  $textLink?: boolean
}>`
  ${DefaultStyle}

  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
  cursor: pointer;
  ${T.misc.link`500`};

  ${(props) => props.$fullWidth && FullWidthStyle}
  ${(props) => props.$outline && OutlineStyle}
  ${(props) => props.$textLink && TextLinkStyle}
`

const StyledLink = styled(DefaultLink)`
  ${BaseStyle};
`

const StyledScrollLink = styled(Link)`
  ${BaseStyle};
`

const Button =
  (WrappedComponent) =>
  ({
    children,
    primaryColor = "var(--burnt-orange)",
    secondaryColor = "#fff",
    outline,
    fullWidth,
    textLink,
    ...rest
  }) => {
    return (
      <WrappedComponent
        $primary={primaryColor}
        $secondary={secondaryColor}
        $outline={outline}
        $fullWidth={fullWidth}
        $textLink={textLink}
        {...rest}
      >
        {children}
        {textLink && <Chevron color={primaryColor} />}
      </WrappedComponent>
    )
  }

Button.Link = withVariants(Button(StyledLink))

Button.ScrollLink = withVariants(Button(StyledScrollLink))

export interface IContentfulButton {
  __typename: "ContentfulButton"
  url: string
  text: string
  colour?: {
    hex: string
  }
  appearance: "Solid" | "Outline"
  variants: IStyleVariant | any[]
}

export const ContentfulButton = ({
  url,
  text,
  colour,
  appearance,
  variants,
}: Omit<IContentfulButton, "__typename">) =>
  // contact-form indicates we want to scroll page
  url === "#contact-form" ? (
    <Button.ScrollLink
      to="contact-form"
      smooth
      duration={1000}
      outline={appearance === "Outline"}
      variants={variants}
    >
      {text}
    </Button.ScrollLink>
  ) : (
    <Button.Link
      to={normalizeForwardSlashes(url)}
      primaryColor={colour?.hex}
      outline={appearance === "Outline"}
      variants={variants}
    >
      {text}
    </Button.Link>
  )

export const ButtonFragment = graphql`
  fragment ContentfulButton on ContentfulButton {
    contentful_id
    url
    text
    colour {
      hex
    }
    appearance
  }
`
export const BUTTON_TYPENAME = "ContentfulButton"
export default Button
